import { Link } from "react-router-dom";
import { Clock, ArrowRight, Calendar, User } from "lucide-react";
import { motion } from "framer-motion";
import { Article, categoryColors } from "@/data/blogContent";

interface ArticleCardProps {
  article: Article;
  featured?: boolean;
  index?: number;
}

export const ArticleCard = ({ article, featured = false, index = 0 }: ArticleCardProps) => {
  const categoryClass = categoryColors[article.category] || "bg-gray-100 text-gray-800";

  if (featured) {
    return (
      <motion.article
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 group"
      >
        <Link to={`/blog/${article.slug}`} className="block md:flex">
          {/* Image */}
          <div className="md:w-1/2 aspect-[16/9] md:aspect-auto overflow-hidden relative">
            <img
              src={article.image}
              alt={article.title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-autosur-navy/60 group-hover:bg-autosur-navy/40 transition-colors" />
          </div>

          {/* Content */}
          <div className="md:w-1/2 p-6 md:p-8 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded-full text-xs font-heading font-semibold ${categoryClass}`}>
                {article.category}
              </span>
              <span className="flex items-center gap-1 text-muted-foreground text-sm font-body">
                <Clock className="w-4 h-4" />
                {article.readTime}
              </span>
            </div>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-4 group-hover:text-autosur-blue transition-colors">
              {article.title}
            </h2>

            <p className="font-body text-muted-foreground mb-6 line-clamp-3">
              {article.excerpt}
            </p>

            <div className="flex items-center justify-between">
              <span className="font-body text-sm text-muted-foreground">
                {article.publishedAt}
              </span>
              <span className="inline-flex items-center gap-2 text-autosur-blue font-heading font-semibold group-hover:gap-3 transition-all">
                Lire l'article
                <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </div>
        </Link>
      </motion.article>
    );
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 group hover:shadow-lg hover:border-autosur-blue/30 transition-all flex flex-col"
    >
      <Link to={`/blog/${article.slug}`} className="block flex-1 flex flex-col">
        {/* Top gradient bar */}
        <div className="h-1 bg-autosur-blue" />

        {/* Image */}
        {article.image && (
          <div className="aspect-[16/9] overflow-hidden relative">
            <img
              src={article.image}
              alt={article.title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-autosur-navy/60 group-hover:bg-autosur-navy/40 transition-colors" />
          </div>
        )}

        {/* Content */}
        <div className="p-5 flex-1 flex flex-col">
          {/* Meta line: category + date + read time */}
          <div className="flex items-center flex-wrap gap-2 mb-3">
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-heading font-semibold ${categoryClass}`}>
              {article.category}
            </span>
            <span className="flex items-center gap-1 text-muted-foreground text-xs font-body">
              <Calendar className="w-3 h-3" />
              {article.publishedAt}
            </span>
            <span className="flex items-center gap-1 text-muted-foreground text-xs font-body">
              <Clock className="w-3 h-3" />
              {article.readTime}
            </span>
          </div>

          {/* Title */}
          <h3 className="font-heading font-bold text-autosur-navy mb-2 line-clamp-2 group-hover:text-autosur-blue transition-colors">
            {article.title}
          </h3>

          {/* Excerpt */}
          <p className="font-body text-muted-foreground text-sm mb-4 line-clamp-2 flex-1">
            {article.excerpt}
          </p>

          {/* Footer: author + CTA */}
          <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
            <span className="flex items-center gap-1 font-body text-xs text-muted-foreground">
              <User className="w-3 h-3" />
              {article.author}
            </span>
            <span className="inline-flex items-center gap-1 text-autosur-blue font-heading font-semibold text-sm group-hover:gap-2 transition-all">
              Lire
              <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
};
